import type * as React from "react";
import { Moon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import type { PresencePersonVM } from "@/types/homey";

interface PresenceBadgeProps extends Omit<React.ComponentProps<"div">, "children"> {
  person: PresencePersonVM;
}

/**
 * Compact per-person presence chip: avatar (image or initials fallback) with a
 * home/away status dot and an optional "asleep" moon hint. Names/avatars are
 * rendered from whatever the Homey API returns at runtime; this component never
 * hardcodes household identity.
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function PresenceBadge({ person, className, ...props }: PresenceBadgeProps) {
  const statusLabel = person.asleep ? "asleep" : person.present ? "home" : "away";
  const label = `${person.name || "Unknown"}: ${statusLabel}`;

  return (
    <div
      data-slot="presence-badge"
      title={label}
      aria-label={label}
      className={cn("relative inline-flex", className)}
      {...props}
    >
      <Avatar
        className={cn(
          "size-8 ring-2 ring-background transition-opacity",
          // Away members are dimmed; present members are full opacity.
          person.present ? "opacity-100" : "opacity-40 grayscale",
        )}
      >
        {person.avatar && <AvatarImage src={person.avatar} alt="" />}
        <AvatarFallback className="text-xs font-medium">{initials(person.name)}</AvatarFallback>
      </Avatar>

      {/* Status dot: present (energy/green) vs away (muted). */}
      <span
        aria-hidden
        className={cn(
          "absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full ring-2 ring-background",
          person.present ? "bg-domain-energy" : "bg-muted-foreground",
        )}
      />

      {/* Asleep hint overlaps the dot corner when the person is sleeping. */}
      {person.present && person.asleep && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-1 flex size-3 items-center justify-center rounded-full bg-domain-climate text-domain-climate-foreground [&_svg]:size-2"
        >
          <Moon />
        </span>
      )}
    </div>
  );
}
